/**
 * 岗位用户管理对话框
 * 支持查看、添加、移除岗位用户，设置主岗位
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, Table, Button, Space, Tag, message, Popconfirm,
  Transfer, Spin, Input
} from 'antd';
import { StarOutlined, DeleteOutlined, StarFilled, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { TransferItem } from 'antd/es/transfer';
import { getPositionUsers, removePosition, setPrimaryPosition, batchAssignPosition } from '../../api/position';
import { getUserList } from '../../api/user';
import type { PositionInfo, User } from '../../api/types';

interface PositionUsersProps {
  visible: boolean;
  position: PositionInfo | null;
  onCancel: () => void;
  onRefresh?: () => void;
}

interface PositionUser {
  id: number;
  userId: number;
  username: string;
  realName: string;
  departmentName: string;
  isPrimary: boolean;
  joinedAt: string;
}

interface UserTransferItem extends TransferItem {
  key: string;
  username: string;
  realName: string;
  departmentName?: string;
}

const PositionUsers: React.FC<PositionUsersProps> = ({
  visible,
  position,
  onCancel,
  onRefresh,
}) => {
  const [list, setList] = useState<PositionUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // 添加用户弹窗相关
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [allUsers, setAllUsers] = useState<UserTransferItem[]>([]);
  const [targetKeys, setTargetKeys] = useState<string[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [addingUsers, setAddingUsers] = useState(false);

  // 获取岗位用户列表
  const fetchUsers = useCallback(async () => {
    if (!position) return;

    setLoading(true);
    try {
      const res = await getPositionUsers(position.id, {
        page: pagination.current,
        page_size: pagination.pageSize,
      });
      if (res?.data) {
        setList(res.data.list || []);
        setTotal(res.data.total || 0);
      }
    } catch (error) {
      console.error('获取岗位用户失败', error);
    } finally {
      setLoading(false);
    }
  }, [position, pagination.current, pagination.pageSize]);

  // 获取所有用户列表（用于添加用户）
  const fetchAllUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await getUserList({ pageSize: 500 });
      if (res?.data) {
        const data = res.data as any;
        const users: User[] = Array.isArray(data) ? data : data.data || data.list || [];

        // 获取当前岗位已有用户的ID
        const existingUserIds = new Set(list.map(u => String(u.userId)));

        // 转换为Transfer数据格式
        const transferData: UserTransferItem[] = users.map((user: User) => ({
          key: String(user.id),
          username: user.username,
          realName: user.realName || user.username,
          departmentName: user.deptName || '-',
          disabled: existingUserIds.has(String(user.id)),
        }));

        setAllUsers(transferData);
        // 设置已有用户为已选中（但disabled）
        setTargetKeys(Array.from(existingUserIds));
      }
    } catch (error) {
      console.error('获取用户列表失败', error);
      message.error('获取用户列表失败');
    } finally {
      setLoadingUsers(false);
    }
  }, [list]);

  useEffect(() => {
    if (visible && position) {
      fetchUsers();
    }
  }, [visible, position, fetchUsers]);

  // 打开添加用户弹窗时加载用户列表
  useEffect(() => {
    if (addModalVisible) {
      fetchAllUsers();
    }
  }, [addModalVisible, fetchAllUsers]);

  // 移除用户
  const handleRemove = async (userId: number) => {
    if (!position) return;

    try {
      await removePosition({ user_id: userId, position_id: position.id });
      message.success('移除成功');
      fetchUsers();
      onRefresh?.();
    } catch (error) {
      console.error('移除失败', error);
    }
  };

  // 设置主岗位
  const handleSetPrimary = async (userId: number) => {
    if (!position) return;

    try {
      await setPrimaryPosition({ user_id: userId, position_id: position.id });
      message.success('设置成功');
      fetchUsers();
    } catch (error) {
      console.error('设置失败', error);
    }
  };

  // 打开添加用户弹窗
  const handleOpenAddModal = () => {
    setAddModalVisible(true);
  };

  // 关闭添加用户弹窗
  const handleCloseAddModal = () => {
    setAddModalVisible(false);
    setTargetKeys([]);
  };

  // Transfer选择变化
  const handleTransferChange = (newTargetKeys: React.Key[], _direction: any, _moveKeys: any) => {
    // 保留已有用户（disabled的）
    const existingUserIds = new Set(list.map(u => String(u.userId)));
    const filteredKeys = newTargetKeys.map(String).filter(key => !existingUserIds.has(key));
    setTargetKeys([...Array.from(existingUserIds), ...filteredKeys]);
  };

  // 批量添加用户到岗位
  const handleAddUsers = async () => {
    if (!position) return;

    // 获取新添加的用户ID（排除已有用户）
    const existingUserIds = new Set(list.map(u => String(u.userId)));
    const newUserIds = targetKeys.filter(key => !existingUserIds.has(key));

    if (newUserIds.length === 0) {
      message.warning('请选择要添加的用户');
      return;
    }

    setAddingUsers(true);
    try {
      await batchAssignPosition({
        user_ids: newUserIds.map(id => Number(id)),
        position_id: position.id,
      });
      message.success(`成功添加 ${newUserIds.length} 名用户`);
      handleCloseAddModal();
      fetchUsers();
      onRefresh?.();
    } catch (error) {
      console.error('添加用户失败', error);
      message.error('添加用户失败');
    } finally {
      setAddingUsers(false);
    }
  };

  // Transfer自定义渲染项
  const renderTransferItem = (item: UserTransferItem) => (
    <span>
      <span style={{ fontWeight: 500 }}>{item.realName}</span>
      <span style={{ color: '#999', marginLeft: 8 }}>({item.username})</span>
      {item.departmentName && (
        <span style={{ color: '#999', marginLeft: 8 }}>- {item.departmentName}</span>
      )}
    </span>
  );

  // Transfer过滤搜索
  const filterOption = (inputValue: string, option: UserTransferItem) => {
    const searchValue = inputValue.toLowerCase();
    return (
      option.username.toLowerCase().includes(searchValue) ||
      (option.realName || '').toLowerCase().includes(searchValue) ||
      (option.departmentName || '').toLowerCase().includes(searchValue)
    );
  };

  const columns: ColumnsType<PositionUser> = [
    {
      title: '用户名',
      dataIndex: 'username',
      width: 120,
    },
    {
      title: '姓名',
      dataIndex: 'realName',
      width: 100,
    },
    {
      title: '部门',
      dataIndex: 'departmentName',
      width: 150,
    },
    {
      title: '主岗位',
      dataIndex: 'isPrimary',
      width: 80,
      render: (isPrimary) => (
        isPrimary ? (
          <Tag color="gold" icon={<StarFilled />}>主岗位</Tag>
        ) : null
      ),
    },
    {
      title: '加入时间',
      dataIndex: 'joinedAt',
      width: 160,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          {!record.isPrimary && (
            <Button
              type="link"
              icon={<StarOutlined />}
              size="small"
              onClick={() => handleSetPrimary(record.userId)}
            >
              设为主岗
            </Button>
          )}
          <Popconfirm
            title="确定要移除此用户吗?"
            onConfirm={() => handleRemove(record.userId)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              移除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={`岗位人员 - ${position?.name || ''}`}
        open={visible}
        onCancel={onCancel}
        width={900}
        footer={[
          <Button key="add" type="primary" icon={<PlusOutlined />} onClick={handleOpenAddModal}>
            添加人员
          </Button>,
          <Button key="close" onClick={onCancel}>
            关闭
          </Button>,
        ]}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={list}
          pagination={{
            ...pagination,
            total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize });
            },
          }}
          loading={loading}
          size="small"
        />
      </Modal>

      {/* 添加用户弹窗 */}
      <Modal
        title={`添加人员到岗位 - ${position?.name || ''}`}
        open={addModalVisible}
        onCancel={handleCloseAddModal}
        onOk={handleAddUsers}
        confirmLoading={addingUsers}
        width={800}
        okText="确认添加"
        cancelText="取消"
      >
        <Spin spinning={loadingUsers}>
          <Transfer
            dataSource={allUsers}
            targetKeys={targetKeys}
            onChange={handleTransferChange}
            render={renderTransferItem}
            filterOption={filterOption}
            showSearch
            listStyle={{
              width: 340,
              height: 400,
            }}
            titles={['待选用户', '已选用户']}
            locale={{
              itemUnit: '人',
              itemsUnit: '人',
              searchPlaceholder: '搜索用户名/姓名/部门',
            }}
          />
          <div style={{ marginTop: 12, color: '#999', fontSize: 12 }}>
            提示：灰色用户表示已在该岗位中
          </div>
        </Spin>
      </Modal>
    </>
  );
};

export default PositionUsers;
